import React, { useEffect, useState } from 'react';
import { BarList, Btn, KPI, KPIRow, Msg, Section, fmtDate } from '../../kit';
import { EmailPreview, Labeled, Modal, fmtFrom, inputCls } from '../emailKit';
import { LIFECYCLE_KEYS, LIFECYCLE_LABEL, toLifecycle } from '../../tags/shared';
import { Content, EXCLUDED_LABEL, labelize, num } from './shared';

/**
 * Steps 3 and 4 — look, then send.
 *
 * The preview is the server's: the exact count after unticking, who is left
 * out and why, and the rendered e-mail for a few real recipients (switchable)
 * with any placeholder that came out blank for that person flagged, so an
 * empty {organisation} is caught before 2,000 people see it. Send is armed
 * only for the request that was previewed; the confirm makes you type the
 * count, and the server recounts and refuses if it moved.
 */
export const PreviewStep: React.FC<{
  preview: any; stale: boolean; busy: string; cap: number;
  content: Content; subjectFallback: string; summary: string[];
  /** Why Send is not armed ('' = ready). */
  blocker: string;
  onSend: () => void; onDryRun: () => void;
  /** Preview again with this recipient rendered first. */
  onRenderFor: (email: string) => void;
}> = ({ preview, stale, busy, cap, content, subjectFallback, summary, blocker, onSend, onDryRun, onRenderFor }) => {
  const [idx, setIdx] = useState(0);
  const [who, setWho] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => { setIdx(0); }, [preview]);

  const count = Number(preview?.count ?? 0);
  const excluded: Record<string, number> = preview?.excluded || {};
  const samples: any[] = preview?.samples || [];
  const cur = samples[Math.min(idx, samples.length - 1)] || null;
  const unknown: string[] = preview?.placeholders_unknown || [];
  const overCap = !!preview?.over_cap || count > cap;
  const env = preview?.envelope || {};
  const typedOk = typed.replace(/[,\s]/g, '') === String(count);
  // Who is missing a value, and what they get instead (placeholder_report).
  const phRows: any[] = preview?.placeholders?.placeholders || [];
  const phMissing = phRows.filter(r => r.kind === 'person' && Number(r.missing) > 0);
  const fallbackOf: Record<string, string> = Object.fromEntries(
    phRows.filter(r => r.kind === 'person').map(r => [r.name, String(r.fallback ?? '')]));
  const toPlaceholders = () => document.getElementById('campaign-placeholders')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const missingLine = (r: any) => (
    <span key={r.name}>
      {`{${r.name}}`} {num(r.missing)} →{' '}
      {r.fallback ? `“${r.fallback}”` : <b className="text-red-700">left blank</b>}
    </span>
  );
  // Who the audience matched but the send leaves out, by reason — the confirm
  // states it, so 262 picked and 261 sent never read as a mismatch.
  const leftOut = Object.entries(excluded).filter(([, v]) => Number(v) > 0);
  const leftOutTotal = leftOut.reduce((a, [, v]) => a + Number(v), 0);
  const send = () => { setConfirmOpen(false); setTyped(''); onSend(); };
  const byLifecycle = (() => {
    const by: Record<string, number> = {};
    Object.entries(preview?.by_lifecycle || preview?.by_state || {}).forEach(([k, v]) => {
      const lk = toLifecycle(k);
      by[lk] = (by[lk] || 0) + Number(v || 0);
    });
    return LIFECYCLE_KEYS.map(k => ({ label: LIFECYCLE_LABEL[k], value: by[k] || 0 })).filter(i => i.value > 0);
  })();

  return (
    <>
      <Section title="3 · Preview"
        right={preview?.generated_at && <span className="text-xs text-grayscale-1100">as of {fmtDate(preview.generated_at)}</span>}>
        {stale && <Msg kind="error">The audience or the content changed since this preview — preview again before sending.</Msg>}
        <KPIRow>
          <KPI label="Will receive" value={num(count)} hint={overCap ? `over the ${num(cap)} cap` : content.name || undefined} />
          <KPI label="Matched" value={num(preview?.matched)} hint="before exclusions" />
          {Object.entries(excluded).filter(([, v]) => Number(v) > 0).map(([k, v]) => (
            <KPI key={k} label={`Left out · ${EXCLUDED_LABEL[k] || labelize(k)}`} value={num(v)} />
          ))}
        </KPIRow>
        {overCap && (
          <Msg kind="error">One campaign is capped at {num(cap)} recipients. Narrow the audience and send in more than one.</Msg>
        )}
        {count === 0 && <Msg kind="error">Nobody is left to send to.</Msg>}
        {unknown.length > 0 && (
          <Msg kind="error">
            We cannot fill {unknown.map(u => `{${u}}`).join(', ')} for anyone — use a placeholder from the list,
            or remove the braces. It cannot be sent like this.
          </Msg>
        )}
        {phMissing.length > 0 && (
          <div className="text-xs rounded-lg px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800">
            {num(preview.placeholders.people_with_missing)} of the {num(preview.placeholders.checked)} recipients are
            missing a value, and get the fallback instead:{' '}
            {phMissing.map((r, i) => <React.Fragment key={r.name}>{i > 0 && ' · '}{missingLine(r)}</React.Fragment>)}.{' '}
            <button type="button" onClick={toPlaceholders} className="underline underline-offset-2">
              Change what they get, or see who they are (step 2)
            </button>
          </div>
        )}
        {!preview?.placeholders && (preview?.placeholders_unfilled || []).length > 0 && (
          <div className="text-xs text-amber-800">
            No value for {preview.placeholders_unfilled.map((p: string) => `{${p}}`).join(', ')} in a group send —
            it renders with the template's default or blank. Check the samples.
          </div>
        )}
        {byLifecycle.length > 0 && count > 0 && <BarList items={byLifecycle} total={count} />}

        {samples.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-grayscale-1100 mr-1">Rendered for:</span>
              {samples.map((s, i) => (
                <button key={i} type="button" onClick={() => setIdx(i)}
                  title={(s.blank || []).length ? `Blank for this person: ${s.blank.map((b: string) => `{${b}}`).join(', ')}` : undefined}
                  className={`px-2.5 py-1 rounded-full text-xs ${i === idx
                    ? 'bg-primary-500 text-grayscale-100' : 'border border-grayscale-300 text-grayscale-1200'}`}>
                  {i + 1} · {s.to || 'sample values'}{(s.blank || []).length ? ' ⚠' : ''}
                </button>
              ))}
              <form className="flex items-center gap-1 ml-2"
                onSubmit={e => { e.preventDefault(); if (who.trim()) onRenderFor(who.trim()); }}>
                <input value={who} onChange={e => setWho(e.target.value)} placeholder="another recipient's e-mail"
                  className={`${inputCls} !h-8 !w-56`} aria-label="Render for this recipient" />
                <Btn small disabled={!who.trim() || busy !== ''}>Render for them</Btn>
              </form>
            </div>
            {cur?.error ? (
              <Msg kind="error">Could not render the e-mail: {cur.error}</Msg>
            ) : cur && (
              <EmailPreview subject={String(cur.subject ?? '')} html={String(cur.html ?? '')}
                text={cur.text ?? undefined}
                from={fmtFrom(cur.from_name, cur.from_email)} replyTo={cur.reply_to || undefined}
                to={cur.to || undefined} plainOnly={cur.send_html === false} height={480}
                banner={<div className="flex flex-col gap-1 text-xs text-grayscale-1100">
                  <span>
                    {cur.sample_values
                      ? 'Nobody matches, so this is rendered with sample values.'
                      : `Exactly what ${cur.name || cur.to} gets, except that links to universitytech.io get campaign tracking (utm_…) added when it is sent.`}
                  </span>
                  {(cur.blank || []).length > 0 && (
                    <span className="text-amber-800">
                      Blank for this person: {cur.blank.map((b: string) => `{${b}}`).join(', ')}
                    </span>
                  )}
                  {(cur.fell_back || []).length > 0 && (
                    <span className="text-amber-800">
                      No value for this person, so the fallback is used:{' '}
                      {cur.fell_back.map((b: string) => `{${b}} → “${fallbackOf[b] ?? ''}”`).join(', ')}
                    </span>
                  )}
                  {((env.cc || []).length > 0 || (env.bcc || []).length > 0) && (
                    <span>
                      {(env.cc || []).length > 0 && <>CC {env.cc.join(', ')} </>}
                      {(env.bcc || []).length > 0 && <>· BCC {env.bcc.join(', ')}</>}
                    </span>
                  )}
                </div>} />
            )}
          </div>
        )}
      </Section>

      <Section title="4 · Send">
        {blocker && !stale && <div className="text-xs text-red-700">{blocker}</div>}
        <div className="flex items-center gap-3 flex-wrap">
          <Btn kind="danger" onClick={() => { setTyped(''); setConfirmOpen(true); }} disabled={!!blocker || stale}>
            {busy === 'send' ? 'Starting…' : `Send to ${num(count)} ${count === 1 ? 'person' : 'people'}…`}
          </Btn>
          <Btn onClick={onDryRun} disabled={!!blocker || stale}
            title="Runs every server check (count, cap, content, sender) and sends nothing">
            {busy === 'dry' ? 'Checking…' : 'Dry run'}
          </Btn>
          <span className="text-xs text-grayscale-1100">
            Dry run re-checks the count, the content and the sender without sending or recording anything.
          </span>
        </div>
      </Section>

      {confirmOpen && (
        <Modal title={`Send “${content.name || subjectFallback}” to ${num(count)} ${count === 1 ? 'person' : 'people'}?`}
          onClose={() => setConfirmOpen(false)}
          footer={<>
            <Btn onClick={() => setConfirmOpen(false)}>Cancel</Btn>
            <Btn kind="danger" onClick={send} disabled={!typedOk}>Send {num(count)} e-mail{count === 1 ? '' : 's'}</Btn>
          </>}>
          <p>
            <b>{num(count)}</b> {count === 1 ? 'person gets' : 'people get'} a real e-mail,{' '}
            <b>“{String(samples[0]?.subject || subjectFallback)}”</b>, sent in batches of 50. It cannot be recalled.
          </p>
          <div className="text-xs text-grayscale-1100 flex flex-col gap-0.5">
            <span>From: {fmtFrom(samples[0]?.from_name, samples[0]?.from_email) || '—'}
              {samples[0]?.reply_to ? ` · Reply-To: ${samples[0].reply_to}` : ''}</span>
            {((env.cc || []).length > 0 || (env.bcc || []).length > 0) && (
              <span className="text-amber-800">
                CC / BCC: {[...(env.cc || []), ...(env.bcc || [])].join(', ')} — {num(count)} copies each.
              </span>
            )}
          </div>
          <ul className="text-xs text-grayscale-1100 list-disc pl-5">
            {summary.map((s, i) => <li key={i}>{s}</li>)}
            {leftOut.length > 0 && (
              <li className="text-amber-800">
                {num(preview?.matched)} matched → {num(count)} get it: {num(leftOutTotal)} left out
                ({leftOut.map(([k, v]) => `${num(v)} ${(EXCLUDED_LABEL[k] || labelize(k)).toLowerCase()}`).join(', ')}).
              </li>
            )}
          </ul>
          {phMissing.length > 0 && (
            <div className="text-xs text-amber-800">
              Missing values: {phMissing.map((r, i) => <React.Fragment key={r.name}>{i > 0 && ' · '}{missingLine(r)}</React.Fragment>)}.
            </div>
          )}
          <Labeled label={`Type ${count} to confirm`}>
            <input autoFocus value={typed} onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && typedOk) send(); }}
              className={`${inputCls} !w-40`} inputMode="numeric" />
          </Labeled>
          <p className="text-[11px] text-grayscale-1100">
            If the audience has changed since the preview, the server refuses and you preview again.
          </p>
        </Modal>
      )}
    </>
  );
};
